import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { InputAdornment, Link, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { FormProvider, TextField } from 'components/Form';
import { isoToDateString } from 'utils/dateFormatter';

const detailsTabName = 'details';

const LinkField = ({ label, value, to }) => (
  <Stack gap={0.5} mb={2}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {to ? (
      <Link component={RouterLink} to={to} variant="body2" fontWeight={600}>
        {value}
      </Link>
    ) : (
      <Typography variant="body2" fontWeight={600}>
        {value || '-'}
      </Typography>
    )}
  </Stack>
);

export const ApplicationDetailsTab = ({ currentTab, application, links }) => {
  const methods = useForm({
    defaultValues: { firstName: '', lastName: '', pricePerHour: '', motivation: '' },
  });

  useEffect(() => {
    methods.reset({
      firstName: application?.first_name ?? '',
      lastName: application?.last_name ?? '',
      pricePerHour: application?.price_per_hour ?? '',
      motivation: application?.motivation ?? '',
    });
  }, [application, methods]);

  if (currentTab !== detailsTabName || !application) return null;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" gap={4} py={2}>
        <Stack flex={1}>
          <Stack direction="row" gap={2}>
            <TextField name="firstName" label="First name" type="text" sx={{ mb: 2 }} fullWidth disabled />
            <TextField name="lastName" label="Last name" type="text" sx={{ mb: 2 }} fullWidth disabled />
          </Stack>
          <TextField
            name="pricePerHour"
            label="Price per hour"
            type="text"
            sx={{ mb: 2 }}
            fullWidth
            disabled
            InputProps={{ endAdornment: <InputAdornment position="end">SEK/h</InputAdornment> }}
          />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Motivation
          </Typography>
          <TextField
            name="motivation"
            type="text"
            fullWidth
            disabled
            multiline
            minRows={4}
            sx={{ '& .MuiInputBase-root, .MuiInputBase-input': { p: 0 } }}
          />
        </Stack>
        <Stack flex={1}>
          <LinkField label="Job ad" value={application.ad_headline} to={links.jobAd} />
          <LinkField label="Supplier" value={application.company_name} to={links.supplier} />
          <LinkField label="Buyer" value={application.buyer_name} to={links.buyer} />
          {application.owner_supplier_name && (
            <LinkField label="Posted by" value={links.postedBy.name} to={links.postedBy.url} />
          )}
          <LinkField label="Contact" value={application.contact_user_name} to={links.contact} />
          {application.preferred_location_name && (
            <LinkField label="Preferred location" value={application.preferred_location_name} />
          )}
          <LinkField
            label={links.isApplicant ? 'Date sent' : 'Date received'}
            value={isoToDateString(application.created_on, true)}
          />
        </Stack>
      </Stack>
    </FormProvider>
  );
};
